import {
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useSelector } from "@tanstack/react-store";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserContext } from "@/contexts/UserContext/index.context";
import { type SelectedOption } from "@/types/models/delivery.types";
import DeliveryPrice from "../../shared/components/DeliveryPrice";
import TotalPrice from "../../shared/components/TotalPrice";
import { OptionList } from "../components/OptionList";
import type { DeliveryFormType } from "../types";

interface Props {
  form: DeliveryFormType;
  type: string;
  isAdmin: boolean;
}

export function PriceOptionsSection({ form, type, isAdmin }: Props) {
  const { t } = useTranslation();
  const { userState } = useUserContext();

  const isOkm = type === "okm";
  const isRestitution = type === "two-way-delivery";

  const [standardPrice, setStandardPrice] = useState(0);
  const [restitutionPrice, setRestitutionPrice] = useState(0);

  const formOrgId = useSelector(form.store, (s) => s.values.step0?.selectedOrganizationId);
  const values = useSelector(form.store, (s) => s.values);
  const resolvedOrgId = formOrgId || userState?.organizationId;
  const organizationId = resolvedOrgId != null ? String(resolvedOrgId) : "";

  const categories = isOkm
    ? [{ value: "OKM", name: t("delivery.form.category.okm") }]
    : [
        { value: "STANDARD", name: t("delivery.form.category.standard") },
        { value: "EXPRESS", name: t("delivery.form.category.express") },
      ];

  return (
    <Paper elevation={4} sx={paperSx}>
      <Grid container direction="row" spacing={4}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5" textTransform="capitalize">
            {t("delivery.form.price-and-options")}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={dividerSx} />
        </Grid>

        {/* Category + Options */}
        <Grid container size={{ xs: 12, md: isRestitution ? 12 : 6 }}>
          <Grid size={{ xs: 12 }}>
            <form.AppField name="lastStep.category">
              {(field) => (
                <FormControl variant="standard" fullWidth disabled={isOkm}>
                  <InputLabel>Categorie de la mission</InputLabel>
                  <Select
                    value={field.state.value || (isOkm ? "OKM" : "STANDARD")}
                    onChange={(e) => field.handleChange(e.target.value)}
                    label="Categorie de la mission"
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </form.AppField>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <form.AppField name="lastStep.selectedOptions.selected">
              {(field) => {
                const selectedOptions: SelectedOption[] = field.state.value ?? [];
                const selectedIds = new Set(selectedOptions.map((o) => Number(o.id)));

                return (
                  <OptionList
                    organizationId={organizationId}
                    selectedIds={selectedIds}
                    onToggle={(option) => {
                      const isSelected = selectedIds.has(option.id);
                      const next = isSelected
                        ? selectedOptions.filter((o) => Number(o.id) !== option.id)
                        : [
                            ...selectedOptions,
                            {
                              id: String(option.id),
                              name: option.name,
                              price: option.dynamicPricing ? 0 : option.clientPrice,
                            } satisfies SelectedOption,
                          ];
                      field.handleChange(next);
                    }}
                  />
                );
              }}
            </form.AppField>
          </Grid>
        </Grid>
        {/* Price (admin only) */}
        {isAdmin && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <DeliveryPrice
                title={t("delivery.form.delivery-details")}
                fromPlaceId={values.step1.fromAddress.place_id}
                toPlaceId={values.step1.toAddress.place_id}
                carType={values.step1.vehicle.carType || "A"}
                category={values.lastStep.category || "STANDARD"}
                numberOfVehicles={1}
                options={values.lastStep.selectedOptions}
                fromCity={values.step1.fromAddress.city}
                toCity={values.step1.toAddress.city}
                onPriceCalculated={(price: number) => setStandardPrice(price)}
              />
            </Grid>
            {isRestitution && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DeliveryPrice
                  title={t("delivery.form.restitution-details")}
                  fromPlaceId={values.step2.toReturnFromAddress.place_id}
                  toPlaceId={values.step2.toReturnToAddress.place_id}
                  carType={values.step2.returnVehicle.carType || "A"}
                  category={values.lastStep.category || "STANDARD"}
                  numberOfVehicles={1}
                  options={values.lastStep.selectedOptions}
                  fromCity={values.step2.toReturnFromAddress.city}
                  toCity={values.step2.toReturnToAddress.city}
                  onPriceCalculated={(price: number) => setRestitutionPrice(price)}
                />
              </Grid>
            )}
            {isRestitution && <TotalPrice totalPrice={standardPrice + restitutionPrice} />}
          </>
        )}
      </Grid>
    </Paper>
  );
}

const paperSx = { backgroundColor: "#E7E6F7", borderRadius: 1, padding: "20px", height: "100%" };
const dividerSx = { bgcolor: "#fe5d8d", marginBottom: 5 };
